import { useQuery } from '@tanstack/react-query';

import { getDisplacementSummary } from '@/service/displacementSummaryService';
import { useAppSelector } from '@/store';

import type { DisplacedSummaryResponse } from '@/service/types/displacementSummaryTypes';

// ─────────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────────

export const displacementSummaryQueryKeys = {
  all: ['displacementSummary'] as const,
  list: (orgId: string) => ['displacementSummary', 'list', orgId] as const,
};

// ─────────────────────────────────────────────────────────────────
// Auto-refresh interval — Matches Xamarin AutoRefreshTimerModel.DisplacementSummaryPage
// Reads from AUTO_REFRESH_SCREENS setting (JSON key "DisplacementSummary")
// ─────────────────────────────────────────────────────────────────

function parseAutoRefreshInterval(autoRefreshScreensJson: string | undefined): number | false {
  if (!autoRefreshScreensJson) return false;
  try {
    const screens = JSON.parse(autoRefreshScreensJson) as Record<string, number>;
    const intervalSec = screens['DisplacementSummary'] ?? 0;
    return intervalSec > 0 ? intervalSec * 1000 : false;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────
// Query Hook — Matches Xamarin DisplaceSummaryPageViewModel.GetDisplaceSummary()
// ─────────────────────────────────────────────────────────────────

export const useDisplacementSummaryQuery = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  // Matches Xamarin: OIC calls require MA_ACCESS_REQUEST to be available for token fetch
  const hasMAConfig = useAppSelector((state) => !!state.global.settings?.MA_ACCESS_REQUEST);
  const autoRefreshScreens = useAppSelector((state) => state.global.settings?.AUTO_REFRESH_SCREENS);

  const refetchInterval = parseAutoRefreshInterval(autoRefreshScreens);

  return useQuery({
    queryKey: displacementSummaryQueryKeys.list(orgId),
    queryFn: async (): Promise<DisplacedSummaryResponse> => {
      return getDisplacementSummary();
    },
    enabled: !!orgId && hasMAConfig,
    staleTime: 2 * 60 * 1000,
    refetchInterval,
  });
};
